import { useState, useRef, useEffect } from "react";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Globe, Loader2, ExternalLink } from "lucide-react";

const SUGGESTIONS = [
  "Bagaimana cara hitung PPh 21 metode TER 2024?",
  "Berapa tarif PPN saat ini dan apa saja yang dikecualikan?",
  "Apa perbedaan PPh 23 dan PPh 4(2) final?",
];

export default function AiAssistant() {
  const { t } = useI18n();
  const [sessionId] = useState(() => `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, citations: data.citations, web: data.web }]);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "AI gagal merespons");
      setMessages((m) => [...m, { role: "assistant", content: "Maaf, terjadi kendala saat menjawab. Silakan coba lagi.", error: true }]);
    } finally { setLoading(false); }
  };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]" data-testid="ai-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" /> {t("ai.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("ai.desc")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setMessages([])} disabled={messages.length === 0} data-testid="ai-clear"><Trash2 className="h-4 w-4 mr-1" />{t("ai.clear")}</Button>
      </div>

      <Card className="flex-1 overflow-auto p-4 space-y-4" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4 text-muted-foreground">
            <Sparkles className="h-10 w-10 text-primary/40" />
            <p className="text-sm max-w-md">{t("ai.empty")}</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-xl">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => ask(s)} className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-secondary transition-colors" data-testid={`ai-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : `bg-secondary rounded-bl-sm ${m.error ? "text-rose-600" : ""}`}`}>
              {m.content}
              {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                <div className="mt-3 pt-2 border-t border-border/60 space-y-1">
                  <p className="text-[11px] font-semibold flex items-center gap-1"><Globe className="h-3 w-3" /> {t("ai.sources")}</p>
                  {m.citations.map((c, j) => (
                    <a key={j} href={c.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                      <ExternalLink className="h-3 w-3 shrink-0" /> [{j + 1}] {c.title}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start" data-testid="ai-loading">
            <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("ai.thinking")}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </Card>

      <div className="mt-3">
        <div className="flex gap-2 items-end">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={2} placeholder={t("ai.placeholder")} className="resize-none" data-testid="ai-input" />
          <Button onClick={() => ask()} disabled={loading || !input.trim()} className="h-[60px]" data-testid="ai-send"><Send className="h-4 w-4" /></Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">{t("ai.disclaimer")}</p>
      </div>
    </div>
  );
}
